/**
 * Scheduled Reports — clinic owner opts into cron-driven email delivery
 * of standard AUDINEXA reports. Emails always go to the caller's account
 * email (no free-form target — prevents data-exfil abuse).
 *
 * Report kinds are fetched from GET /api/scheduled-reports/kinds so this
 * component never hard-codes the catalogue.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Mail, CheckCircle2, AlertCircle, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROOT = `${API}/scheduled-reports`;

export default function ScheduledReportsTab() {
  const { user } = useAuth();
  const [kinds, setKinds] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKind, setBusyKind] = useState(null);
  const [sendingKind, setSendingKind] = useState(null);
  const [flash, setFlash] = useState(null); // { kind, tone, text }

  const canSubscribe = ['clinic_owner', 'accounts', 'super_admin', 'founder']
    .includes(user?.role);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [kRes, sRes] = await Promise.all([
        axios.get(`${ROOT}/kinds`),
        axios.get(`${ROOT}/subscriptions`),
      ]);
      setKinds(kRes.data?.kinds || []);
      setSubs(sRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subFor = (kind) => subs.find((s) => s.report_kind === kind);

  const toggle = async (kind, currentlyActive) => {
    if (busyKind) return;
    setBusyKind(kind);
    setFlash(null);
    try {
      if (currentlyActive) {
        await axios.delete(`${ROOT}/subscribe/${kind}`);
      } else {
        await axios.post(`${ROOT}/subscribe`, { report_kind: kind });
      }
      await load();
      setFlash({ kind, tone: 'ok', text: currentlyActive ? 'Unsubscribed' : 'Subscribed' });
    } catch (e) {
      setFlash({ kind, tone: 'err',
        text: e?.response?.data?.detail || e?.message || 'Save failed' });
    } finally {
      setBusyKind(null);
    }
  };

  const sendNow = async (kind) => {
    if (sendingKind) return;
    setSendingKind(kind);
    setFlash(null);
    try {
      const r = await axios.post(`${ROOT}/send-now`, { report_kind: kind });
      setFlash({
        kind,
        tone: r.data?.ok ? 'ok' : 'err',
        text: r.data?.ok
          ? `Sent to ${r.data.to} (${r.data.status})`
          : (r.data?.status || 'Send failed'),
      });
    } catch (e) {
      setFlash({ kind, tone: 'err',
        text: e?.response?.data?.detail || e?.message || 'Send failed' });
    } finally {
      setSendingKind(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="scheduled-reports-tab">
      <div className="mb-5">
        <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2"
            data-testid="scheduled-reports-heading">
          <CalendarClock size={18} className="text-indigo-600" />
          Scheduled Reports
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Get key AUDINEXA reports delivered to your inbox automatically. Emails
          are sent to <b>{user?.email || 'your account address'}</b> — you can
          unsubscribe anytime from this page.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded px-3 py-2"
             data-testid="scheduled-reports-error">
          {error}
        </div>
      )}

      {!canSubscribe && (
        <div className="mb-4 bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded px-3 py-2"
             data-testid="scheduled-reports-role-gate">
          Only clinic owners / accounts users can manage scheduled reports.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 italic" data-testid="scheduled-reports-loading">
          Loading…
        </div>
      ) : (
        <div className="space-y-3">
          {kinds.map((k) => {
            const sub = subFor(k.kind);
            const active = !!sub?.active;
            const rowFlash = flash?.kind === k.kind ? flash : null;
            return (
              <div
                key={k.kind}
                data-testid={`scheduled-report-card-${k.kind}`}
                className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row md:items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-800"
                          data-testid={`scheduled-report-label-${k.kind}`}>
                      {k.label}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                      {k.frequency}
                    </span>
                    {active && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                            data-testid={`scheduled-report-active-badge-${k.kind}`}>
                        <CheckCircle2 size={10} /> Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{k.description}</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock size={11} /> {k.cadence_human}
                    </span>
                    {sub?.last_sent_at && (
                      <span className="inline-flex items-center gap-1"
                            data-testid={`scheduled-report-last-sent-${k.kind}`}>
                        <Mail size={11} /> Last sent: {new Date(sub.last_sent_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {rowFlash && (
                    <div
                      data-testid={`scheduled-report-flash-${k.kind}`}
                      className={`mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${
                        rowFlash.tone === 'ok'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                          : 'bg-rose-50 border-rose-200 text-rose-800'
                      }`}
                    >
                      {rowFlash.tone === 'ok' ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
                      {rowFlash.text}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    disabled={!canSubscribe || sendingKind === k.kind}
                    onClick={() => sendNow(k.kind)}
                    data-testid={`scheduled-report-send-now-${k.kind}`}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed rounded inline-flex items-center gap-1.5"
                  >
                    {sendingKind === k.kind ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    Send now
                  </button>
                  <button
                    disabled={!canSubscribe || busyKind === k.kind}
                    onClick={() => toggle(k.kind, active)}
                    data-testid={`scheduled-report-toggle-${k.kind}`}
                    className={`px-3 py-1.5 text-xs font-bold rounded shadow-sm disabled:cursor-not-allowed disabled:opacity-60 ${
                      active
                        ? 'bg-rose-600 hover:bg-rose-700 text-white'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {busyKind === k.kind
                      ? '…'
                      : active
                        ? 'Unsubscribe'
                        : 'Subscribe'}
                  </button>
                </div>
              </div>
            );
          })}
          {kinds.length === 0 && (
            <div className="text-sm text-slate-500 italic"
                 data-testid="scheduled-reports-empty">
              No report kinds configured.
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-[11px] text-slate-400">
        AUDINEXA runs these jobs from the platform. Delivery uses your configured
        email provider (see Settings → Integrations).
      </p>
    </div>
  );
}
